// The icons the public menu can draw, and only those: `utils/fontawesome` carries the whole
// dashboard's set, an order of magnitude more glyphs than a menu ever shows.
//
// Imported one file per icon rather than from the package root, and that is what makes the
// shortlist worth anything. `pro-regular-svg-icons/index.mjs` is a single three-megabyte module:
// tree shaking keeps only the icons that are used, but a module is the smallest thing a bundler
// can split, so reading this page's icons out of it would have put every entrypoint's icons in
// one chunk and served the dashboard's to a diner. The per-icon files are separate modules.
//
// The list is pinned by `spec/config/menu_icons_spec.rb` — every service an owner can tick is a
// glyph a diner can meet, so a new entry in `Restaurant::SERVICES` has to arrive here too.
import { library, dom } from "@fortawesome/fontawesome-svg-core"

import { faAirConditioner } from "@fortawesome/pro-regular-svg-icons/faAirConditioner"
import { faBagsShopping } from "@fortawesome/pro-regular-svg-icons/faBagsShopping"
import { faCalendarCheck } from "@fortawesome/pro-regular-svg-icons/faCalendarCheck"
import { faCarSide } from "@fortawesome/pro-regular-svg-icons/faCarSide"
import { faChild } from "@fortawesome/pro-regular-svg-icons/faChild"
import { faClock } from "@fortawesome/pro-regular-svg-icons/faClock"
import { faConciergeBell } from "@fortawesome/pro-regular-svg-icons/faConciergeBell"
import { faDog } from "@fortawesome/pro-regular-svg-icons/faDog"
import { faExclamationCircle } from "@fortawesome/pro-regular-svg-icons/faExclamationCircle"
import { faGift } from "@fortawesome/pro-regular-svg-icons/faGift"
import { faGlassCheers } from "@fortawesome/pro-regular-svg-icons/faGlassCheers"
import { faGlobe } from "@fortawesome/pro-regular-svg-icons/faGlobe"
import { faHeat } from "@fortawesome/pro-regular-svg-icons/faHeat"
import { faImages } from "@fortawesome/pro-regular-svg-icons/faImages"
import { faMagnifyingGlass } from "@fortawesome/pro-regular-svg-icons/faMagnifyingGlass"
import { faMapMarkerAlt } from "@fortawesome/pro-regular-svg-icons/faMapMarkerAlt"
import { faMoonStars } from "@fortawesome/pro-regular-svg-icons/faMoonStars"
import { faMoped } from "@fortawesome/pro-regular-svg-icons/faMoped"
import { faMugHot } from "@fortawesome/pro-regular-svg-icons/faMugHot"
import { faParking } from "@fortawesome/pro-regular-svg-icons/faParking"
import { faPepperHot } from "@fortawesome/pro-regular-svg-icons/faPepperHot"
import { faPhone } from "@fortawesome/pro-regular-svg-icons/faPhone"
import { faPlateUtensils } from "@fortawesome/pro-regular-svg-icons/faPlateUtensils"
import { faRestroom } from "@fortawesome/pro-regular-svg-icons/faRestroom"
import { faSalad } from "@fortawesome/pro-regular-svg-icons/faSalad"
import { faSeedling } from "@fortawesome/pro-regular-svg-icons/faSeedling"
import { faSmoking } from "@fortawesome/pro-regular-svg-icons/faSmoking"
import { faStamp } from "@fortawesome/pro-regular-svg-icons/faStamp"
import { faTicketAlt } from "@fortawesome/pro-regular-svg-icons/faTicketAlt"
import { faUmbrellaBeach } from "@fortawesome/pro-regular-svg-icons/faUmbrellaBeach"
import { faUserShield } from "@fortawesome/pro-regular-svg-icons/faUserShield"
import { faVolumeSlash } from "@fortawesome/pro-regular-svg-icons/faVolumeSlash"
import { faWallet } from "@fortawesome/pro-regular-svg-icons/faWallet"
import { faWheelchair } from "@fortawesome/pro-regular-svg-icons/faWheelchair"
import { faWheelchairMove } from "@fortawesome/pro-regular-svg-icons/faWheelchairMove"
import { faWifi } from "@fortawesome/pro-regular-svg-icons/faWifi"
import { faWineGlass } from "@fortawesome/pro-regular-svg-icons/faWineGlass"
import { faXmark } from "@fortawesome/pro-regular-svg-icons/faXmark"

import { faFacebook } from "@fortawesome/free-brands-svg-icons/faFacebook"
import { faInstagram } from "@fortawesome/free-brands-svg-icons/faInstagram"
import { faLinkedin } from "@fortawesome/free-brands-svg-icons/faLinkedin"
import { faPinterest } from "@fortawesome/free-brands-svg-icons/faPinterest"
import { faXTwitter } from "@fortawesome/free-brands-svg-icons/faXTwitter"

library.add(
  faAirConditioner,
  faBagsShopping,
  faCalendarCheck,
  faCarSide,
  faChild,
  faClock,
  faConciergeBell,
  faDog,
  faExclamationCircle,
  faGift,
  faGlassCheers,
  faGlobe,
  faHeat,
  faImages,
  faMagnifyingGlass,
  faMapMarkerAlt,
  faMoonStars,
  faMoped,
  faMugHot,
  faParking,
  faPepperHot,
  faPhone,
  faPlateUtensils,
  faRestroom,
  faSalad,
  faSeedling,
  faSmoking,
  faStamp,
  faTicketAlt,
  faUmbrellaBeach,
  faUserShield,
  faVolumeSlash,
  faWallet,
  faWheelchair,
  faWheelchairMove,
  faWifi,
  faWineGlass,
  faXmark,
)

// Brands icons
library.add(faFacebook, faInstagram, faLinkedin, faPinterest, faXTwitter)

// One pass over the document instead of `dom.watch()`'s MutationObserver: the menu is rendered
// whole by the server, with no Turbo behind it and nothing that adds an `<i>` afterwards.
dom.i2svg()
